/**
 * AssetAllocationActionPage - Public page for employees to accept/reject asset allocations via email link
 * No authentication required - uses secure token
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { assetAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle, XCircle, AlertTriangle, Package, Laptop, Monitor, Smartphone, Box } from 'lucide-react';

const CATEGORY_ICONS = {
  Laptop: Laptop,
  Desktop: Monitor,
  Monitor: Monitor,
  Mobile: Smartphone,
};

export default function AssetAllocationActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [allocationData, setAllocationData] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [completed, setCompleted] = useState(false);
  const [resultAction, setResultAction] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No token provided. Please use the link from your email.');
      setLoading(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await assetAPI.verifyAllocationToken(token);
      setAllocationData(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid or expired link. Please contact IT support.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!token) return;

    setSubmitting(true);
    try {
      await assetAPI.executeAllocationAction(token, remarks);
      setCompleted(true);
      setResultAction(allocationData.action);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to process your response. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
            <p className="text-muted-foreground">Verifying your request...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-red-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">Unable to Process Request</h2>
            <p className="text-muted-foreground text-center max-w-sm">{error}</p>
            <Button variant="outline" className="mt-6" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completed state
  if (completed) {
    const isAccept = resultAction === 'accept';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className={`w-full max-w-lg ${isAccept ? 'border-green-200' : 'border-orange-200'}`}>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className={`w-20 h-20 rounded-full ${isAccept ? 'bg-green-100' : 'bg-orange-100'} flex items-center justify-center mb-6`}>
              {isAccept ? (
                <CheckCircle className="w-10 h-10 text-green-600" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-orange-600" />
              )}
            </div>
            <h2 className={`text-2xl font-bold ${isAccept ? 'text-green-800' : 'text-orange-800'} mb-2`}>
              {isAccept ? 'Asset Allocation Accepted!' : 'Issue Reported Successfully'}
            </h2>
            <p className="text-muted-foreground text-center max-w-sm mb-6">
              {isAccept
                ? 'Thank you for confirming. The asset(s) have been officially assigned to you. Please take good care of them.'
                : 'Your concern has been recorded. HR/IT Admin will review and contact you shortly.'}
            </p>
            {remarks && (
              <div className="bg-muted/50 p-4 rounded-lg w-full max-w-sm">
                <p className="text-sm text-muted-foreground">Your remarks:</p>
                <p className="text-sm mt-1">{remarks}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main action view
  const isAccept = allocationData?.action === 'accept';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isAccept ? 'bg-green-100' : 'bg-orange-100'}`}>
            {isAccept ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isAccept ? 'Accept Asset Allocation' : 'Report Issue with Allocation'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isAccept
              ? 'Please review the asset details and confirm acceptance'
              : 'Please provide details about the issue'}
          </p>
        </div>

        {/* Allocation Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Allocation Details</CardTitle>
            <CardDescription>
              Allocated on {new Date(allocationData?.allocated_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })} by {allocationData?.allocated_by_name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>Employee:</strong> {allocationData?.employee_name}
              </p>
            </div>

            {/* Assets List */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                Allocated Assets ({allocationData?.assets?.length || 0})
              </h4>
              {allocationData?.assets?.map((asset, idx) => {
                const Icon = CATEGORY_ICONS[asset.category] || Package;
                return (
                  <div key={asset.id || idx} className="flex items-start gap-4 p-4 border rounded-lg bg-white">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Icon className="w-6 h-6 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{asset.name}</h4>
                        <Badge variant="outline">{asset.category}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <p><span className="font-medium">Tag ID:</span> {asset.tag_id || 'N/A'}</p>
                        <p><span className="font-medium">Serial:</span> {asset.serial_number || 'N/A'}</p>
                        {asset.location && <p><span className="font-medium">Location:</span> {asset.location}</p>}
                        {asset.vendor && <p><span className="font-medium">Vendor:</span> {asset.vendor}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Action Card */}
        <Card className={`border-2 ${isAccept ? 'border-green-200' : 'border-orange-200'}`}>
          <CardHeader className={isAccept ? 'bg-green-50' : 'bg-orange-50'}>
            <CardTitle className={`text-lg ${isAccept ? 'text-green-800' : 'text-orange-800'}`}>
              {isAccept ? 'Confirm Acceptance' : 'Report Issue'}
            </CardTitle>
            <CardDescription className={isAccept ? 'text-green-700' : 'text-orange-700'}>
              {isAccept
                ? 'By accepting, you acknowledge responsibility for the care and maintenance of these assets.'
                : 'Please describe the issue with the allocated asset(s).'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {isAccept ? 'Remarks (Optional)' : 'Issue Description'}
                  {!isAccept && <span className="text-red-500">*</span>}
                </label>
                <Textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder={isAccept
                    ? 'Any additional comments or notes...'
                    : 'Please describe the issue with the asset(s)...'}
                  rows={4}
                  data-testid="allocation-remarks-input"
                />
              </div>

              <Button
                onClick={handleAction}
                disabled={submitting || (!isAccept && !remarks.trim())}
                className={`w-full ${isAccept
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-orange-600 hover:bg-orange-700'}`}
                size="lg"
                data-testid="allocation-action-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : isAccept ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept & Acknowledge
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Report Issue
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          This action is being recorded. If you have any questions, please contact IT support.
        </p>
      </div>
    </div>
  );
}
